/**
 * shared/utils/logger.ts
 *
 * Sistema de logs empresarial para CELMAXX POS.
 * Uso: logInfo('ventas', 'Venta creada', { id, total })
 *
 * En DEV  → muestra en consola con colores y timestamps.
 * En PROD → solo logError() escribe (silencioso en info/warn).
 *
 * Módulos reconocidos:
 *   'pos' | 'ventas' | 'caja' | 'turno' | 'sync' |
 *   'inventario' | 'tickets' | 'auth' | 'supabase' | 'general'
 */

// ── Tipos ──────────────────────────────────────────────────────────────────

export type LogModule =
  | 'pos'
  | 'ventas'
  | 'caja'
  | 'turno'
  | 'sync'
  | 'inventario'
  | 'tickets'
  | 'auth'
  | 'supabase'
  | 'general'

export type LogLevel = 'info' | 'warn' | 'error'

export interface LogEntry {
  ts: string // ISO timestamp
  level: LogLevel
  module: LogModule
  msg: string
  data?: unknown
  error?: string // message del Error si aplica
  stack?: string // stack trace si aplica
}

// ── Ambiente ───────────────────────────────────────────────────────────────

const isProd = import.meta.env.PROD === true
// Debug: solo visible si VITE_DEBUG_LOGS=true en .env.local (silencioso en DEV y PROD por defecto)
const DEBUG = import.meta.env.VITE_DEBUG_LOGS === 'true'

// ── Buffer en memoria (últimas 200 entradas) ───────────────────────────────
// Útil para mostrar en un panel de diagnóstico o enviar al servidor.

const LOG_BUFFER_MAX = 200
const _buffer: LogEntry[] = []

export function getLogBuffer(): ReadonlyArray<LogEntry> {
  return _buffer
}

export function clearLogBuffer(): void {
  _buffer.length = 0
}

// ── Colores por módulo (DEV) ───────────────────────────────────────────────

const MODULE_COLORS: Record<LogModule, string> = {
  pos: '#6366f1', // indigo
  ventas: '#22c55e', // green
  caja: '#f59e0b', // amber
  turno: '#3b82f6', // blue
  sync: '#8b5cf6', // violet
  inventario: '#14b8a6', // teal
  tickets: '#ec4899', // pink
  auth: '#64748b', // slate
  supabase: '#1e3a5f', // dark blue
  general: '#94a3b8', // gray
}

const LEVEL_COLORS: Record<LogLevel, string> = {
  info: '#94a3b8',
  warn: '#f59e0b',
  error: '#ef4444',
}

// ── Core ───────────────────────────────────────────────────────────────────

function _log(level: LogLevel, module: LogModule, msg: string, data?: unknown): void {
  const ts = new Date().toISOString()
  const entry: LogEntry = { ts, level, module, msg }

  if (data instanceof Error) {
    entry.error = data.message
    entry.stack = data.stack
  } else if (data !== undefined) {
    entry.data = data
  }

  // Guardar en buffer siempre
  _buffer.push(entry)
  if (_buffer.length > LOG_BUFFER_MAX) _buffer.shift()

  // En PROD: solo errores a consola
  if (isProd && level !== 'error') return

  // En DEV: consola con formato
  const time = ts.slice(11, 23) // HH:MM:SS.mmm
  const prefix = `%c[${module.toUpperCase()}]%c ${time} ${msg}`
  const modStyle = `color:${MODULE_COLORS[module]};font-weight:bold`
  const baseStyle = `color:${LEVEL_COLORS[level]}`

  if (level === 'error') {
    console.error(prefix, modStyle, baseStyle, ...(data ? [data] : []))
  } else if (level === 'warn') {
    console.warn(prefix, modStyle, baseStyle, ...(data ? [data] : []))
  } else {
    console.log(prefix, modStyle, baseStyle, ...(data ? [data] : []))
  }
}

// ── API pública ────────────────────────────────────────────────────────────

/** Info — solo visible en DEV */
export function logInfo(module: LogModule, msg: string, data?: unknown): void {
  _log('info', module, msg, data)
}

/** Warn — visible en DEV, silencioso en PROD */
export function logWarn(module: LogModule, msg: string, data?: unknown): void {
  _log('warn', module, msg, data)
}

/** Error — visible siempre (DEV y PROD) */
export function logError(module: LogModule, msg: string, data?: unknown): void {
  _log('error', module, msg, data)
}

/** Debug — visible solo si VITE_DEBUG_LOGS=true en .env.local */
export function logDebug(module: LogModule, msg: string, data?: unknown): void {
  if (!DEBUG) return
  _log('info', module, `[debug] ${msg}`, data)
}

// ── Helpers especializados ─────────────────────────────────────────────────

/** Log de venta completada */
export function logVenta(saleId: string, total: number, storeCode?: string): void {
  logInfo('ventas', `Venta completada: ${saleId}`, { total, storeCode })
}

/** Log de apertura de turno */
export function logAperturaTurno(shiftId: string, startCash: number): void {
  logInfo('turno', `Turno abierto: ${shiftId}`, { startCash })
}

/** Log de cierre de turno */
export function logCierreTurno(shiftId: string, endCash: number, diff: number): void {
  logInfo('turno', `Turno cerrado: ${shiftId}`, { endCash, diferencia: diff })
}

/** Log de movimiento de caja */
export function logMovimientoCaja(
  type: 'income' | 'expense',
  amount: number,
  concept: string
): void {
  logInfo('caja', `Movimiento ${type}: $${amount}`, { concept })
}

/** Log de error de Supabase */
export function logSupabaseError(operation: string, error: string, table?: string): void {
  logError('supabase', `Error en ${operation}${table ? ` (${table})` : ''}`, { error })
}

/** Log de sync */
export function logSync(event: string, count?: number): void {
  logInfo('sync', event, count !== undefined ? { count } : undefined)
}
